import FileDownload from 'js-file-download';
import { createAsyncAction, createStandardAction } from 'typesafe-actions';
import { ISystemAnalysisRequest, ISystemAnalysisResponse } from '../models/SystemAnalysisData';
import { authService } from 'services/AuthProvider';
import { makeApiUrl } from 'actions/_shared/utils';
import apiAction from 'actions/_shared/apiAction';
import {
  asyncReportJobDownloadUrl,
  asyncReportJobStatusUrl,
  clearAsyncReportJobPolling,
  getAsyncReportJobFailedMessage,
  getAsyncReportJobTimeoutMessage,
  isAsyncReportJobPollingTimedOut,
  notifyAsyncReportJobError,
  notifyAsyncReportJobProcessing,
  scheduleAsyncReportJobPoll,
} from 'actions/_shared/asyncReportJob';
import { getCustomSearchParameters } from 'helpers/date';

import { KonnectAnalytics } from 'KonnectAnalytics/konnectAnalytics';
import { add, format } from 'date-fns';
import { TimeFrameEnum } from 'models/ReportData';
import { UserSystemGroup } from 'store/storeTypes';

export const getSystemAnalysisAC = createAsyncAction(
  'GET_SYSTEM_ANALYSIS_STARTED',
  'GET_SYSTEM_ANALYSIS_SUCCESS',
  'GET_SYSTEM_ANALYSIS_FAILED'
)<ISystemAnalysisRequest, ISystemAnalysisResponse, Error>();

export function getSystemAnalysis({
  day,
  week,
  custom,
  systemGroup,
  isSilent,
  isEverReportedSystems,
  isProductionHours,
}: ISystemAnalysisRequest) {
  const url = makeApiUrl('/api/reports/systems');
  const customTimeframeParameters = getCustomSearchParameters(custom);

  return apiAction({
    request: {
      url,
      method: 'GET',
      params: {
        day,
        week,
        custom: customTimeframeParameters,
        systemGroup,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getSystemAnalysisAC.failure(error));
      },

      onStarted: dispatch => {
        dispatch(getSystemAnalysisAC.request({ isSilent }));
      },
      onSuccess: (data, dispatch) => {
        dispatch(getSystemAnalysisAC.success({ ...data, isEverReportedSystems }));
      },
    },
  });
}

export const getSystemAnalysisNickNamesAC = createAsyncAction(
  'GET_SYSTEM_ANALYSIS_NICK_NAMES_STARTED',
  'GET_SYSTEM_ANALYSIS_NICK_NAMES_SUCCESS',
  'GET_SYSTEM_ANALYSIS_NICK_NAMES_FAILED'
)();
export function getSystemAnalysisNickNames({ isSilent, systems, systemGroup }) {
  const url = makeApiUrl('/api/system-data/systems-name');
  return apiAction({
    request: {
      url,
      method: 'GET',
      data: {
        systems,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getSystemAnalysisNickNamesAC.failure(error));
      },
      onStarted: dispatch => {
        dispatch(getSystemAnalysisNickNamesAC.request({ isSilent }));
      },
      onSuccess: (data, dispatch) => {
        dispatch(getSystemAnalysisNickNamesAC.success(data.nicknameResponse));
      },
    },
  });
}

export const changeNickNameAC = createAsyncAction(
  'CHANGE_NICKNAME_STARTED',
  'CHANGE_NICKNAME_SUCCESS',
  'CHANGE_NICKNAME_FAILED'
)();
export function changeNickName({ serialNumber, nickName, selectedSystemGroup }) {
  const url = makeApiUrl('/api/system-data/system-name');
  return apiAction({
    request: {
      url,
      method: 'POST',
      data: {
        serialNumber,
        nickName,
      },
    },
    logic: {
      onFailed: (error, dispatch) => dispatch(changeNickNameAC.failure(error)),
      onStarted: dispatch => dispatch(changeNickNameAC.request({ isLoading: true })),
      onSuccess: (data, dispatch) =>
        dispatch(changeNickNameAC.success({ data, selectedSystemGroup })),
    },
  });
}
export const setInitialValueToChangeNameField = createStandardAction(
  'SET_INITIAL_VALUE_TO_CHANGE_NAME_FIELD'
)<{ systemName: string }>();
export const setSelectedSystemName = createStandardAction('SET_SELECTED_SYSTEM_NAME')<string>();
export const setSelectedSystemSerialGk = createStandardAction('SET_SELECTED_SYSTEM_SERIAL_GK')<{
  systemSerialGk: string;
}>();
export const setSortByHeaderCell = createStandardAction('SET_SORT_BY_HEADER_CELL_NAME')<any>();

//* Download System Analysis Report

const ERRORS_AND_MAINTENANCE_FILE_SEGMENT = 'errors-and-maintenance-file';

export const downloadSystemAnalysisReportAC = createAsyncAction(
  'DOWNLOAD_SYSTEM_ANALYSIS_REPORT_STARTED',
  'DOWNLOAD_SYSTEM_ANALYSIS_REPORT_SUCCESS',
  'DOWNLOAD_SYSTEM_ANALYSIS_REPORT_FAILED'
)<any, any, any>();

type SystemAnalysisJob = {
  jobId: string;
  SystemGroupType: any;
  analyticsInteractionMetadata: any;
  fileName: string;
  //* namespaced per report, otherwise exports of different reports for the same system group
  //* would share a polling timer and cancel each other
  pollingKey: string;
  startedAt: number;
  attempt: number;
};

const getSystemAnalysisJobPollingKey = (SystemGroupType: string) =>
  `${ERRORS_AND_MAINTENANCE_FILE_SEGMENT}:${SystemGroupType}`;

const failSystemAnalysisJob = (
  dispatch,
  { SystemGroupType, analyticsInteractionMetadata, pollingKey }: Partial<SystemAnalysisJob>,
  errorMessage?: string
) => {
  clearAsyncReportJobPolling(pollingKey);
  if (errorMessage) {
    notifyAsyncReportJobError(dispatch, errorMessage);
  }
  KonnectAnalytics.downloadSystemAnalysisReport({
    result: 'not downloaded, failed',
    ...analyticsInteractionMetadata,
  });
  dispatch(
    downloadSystemAnalysisReportAC.failure({
      selectedSystemGroup: SystemGroupType,
      message: 'something went wrong, try again later',
    })
  );
};

const downloadSystemAnalysisJobFile = (job: SystemAnalysisJob) =>
  apiAction({
    request: {
      url: asyncReportJobDownloadUrl(job.jobId),
      params: { responseType: 'blob' },
    },
    logic: {
      onStarted: () => {},
      onFailed: (error, dispatch) => failSystemAnalysisJob(dispatch, job),
      onSuccess: (data, dispatch, state, status) => {
        clearAsyncReportJobPolling(job.pollingKey);
        FileDownload(data, job.fileName);
        KonnectAnalytics.downloadSystemAnalysisReport({
          result: 'downloaded',
          ...job.analyticsInteractionMetadata,
        });
        dispatch(
          downloadSystemAnalysisReportAC.success({
            selectedSystemGroup: job.SystemGroupType,
            status,
          })
        );
      },
    },
  });

const pollSystemAnalysisJobStatus = (job: SystemAnalysisJob) =>
  apiAction({
    request: {
      url: asyncReportJobStatusUrl(job.jobId),
    },
    logic: {
      onStarted: () => {},
      onFailed: (error, dispatch) => failSystemAnalysisJob(dispatch, job),
      onSuccess: (data, dispatch) => {
        if (data?.status === 'Completed') {
          dispatch(downloadSystemAnalysisJobFile(job));
          return;
        }
        if (data?.status === 'Failed') {
          failSystemAnalysisJob(dispatch, job, data?.errorMessage || getAsyncReportJobFailedMessage());
          return;
        }
        if (isAsyncReportJobPollingTimedOut(job.startedAt)) {
          failSystemAnalysisJob(dispatch, job, getAsyncReportJobTimeoutMessage());
          return;
        }
        const nextJob = { ...job, attempt: job.attempt + 1 };
        notifyAsyncReportJobProcessing(dispatch, nextJob.attempt);
        scheduleAsyncReportJobPoll(nextJob.pollingKey, () =>
          dispatch(pollSystemAnalysisJobStatus(nextJob))
        );
      },
    },
  });

export const downloadSystemAnalysisReport = ({
  week,
  day,
  custom,
  systems,
  SystemGroupType,
  analyticsInteractionMetadata,
}: any) => {
  const url = makeApiUrl(`/api/job-analysis-report/${ERRORS_AND_MAINTENANCE_FILE_SEGMENT}/async`);
  const customTimeframeParameters = getCustomSearchParameters(custom);
  const pollingKey = getSystemAnalysisJobPollingKey(SystemGroupType);
  return apiAction({
    request: {
      url,
      method: 'POST',
      params: {
        week,
        day,
        custom: customTimeframeParameters,
        SystemGroupType,
      },
      data: {
        systems,
      },
    },
    logic: {
      onFailed: (error, dispatch) =>
        failSystemAnalysisJob(dispatch, {
          SystemGroupType,
          analyticsInteractionMetadata,
          pollingKey,
        }),
      onStarted: dispatch => {
        clearAsyncReportJobPolling(pollingKey);
        dispatch(
          downloadSystemAnalysisReportAC.request({
            selectedSystemGroup: SystemGroupType,
          })
        );
      },
      onSuccess: (data, dispatch, state, status) => {
        //* 2 cases - the file is too big and there is no file.
        if (status === 204 || status === 205) {
          KonnectAnalytics.downloadSystemAnalysisReport({
            result:
              status === 204 ? 'not downloaded, no content' : 'not downloaded, file is too big',
            ...analyticsInteractionMetadata,
          });
          dispatch(
            downloadSystemAnalysisReportAC.success({
              selectedSystemGroup: SystemGroupType,
              status,
            })
          );
          return;
        }
        //* the date range suffix is frozen at request time, matching the filters shown when the
        //* user clicked download - the job may still be running once those filters have changed
        const timeframe = state.reports.filterState[SystemGroupType].timeframe;
        const dateStr = getFileName({
          date: state.reports.filterState[SystemGroupType].selectedDate,
          timeframe,
          customTimeframeParameters,
        });
        const job: SystemAnalysisJob = {
          jobId: data?.jobId,
          SystemGroupType,
          analyticsInteractionMetadata,
          fileName: `Events_Error_Maintenance_${dateStr}.xlsx`,
          pollingKey,
          startedAt: Date.now(),
          attempt: 0,
        };
        if (!job.jobId) {
          failSystemAnalysisJob(dispatch, job, getAsyncReportJobFailedMessage());
          return;
        }
        notifyAsyncReportJobProcessing(dispatch, job.attempt);
        scheduleAsyncReportJobPoll(job.pollingKey, () =>
          dispatch(pollSystemAnalysisJobStatus(job))
        );
      },
    },
  });
};
const getFileName = ({ date, timeframe, customTimeframeParameters }) => {
  if (timeframe === TimeFrameEnum.day) {
    const formattedDate = format(date, 'MMMM_d_yyyy');
    const finalString = formattedDate.replace(/\s+/g, '_');
    return finalString;
  }
  if (timeframe === TimeFrameEnum.week) {
    const formattedDate = format(date, 'MMMM_d_yyyy');
    const finalString = formattedDate.replace(/\s+/g, '_');
    const endDate = add(date, { days: 6 });
    const formattedEndDate = format(endDate, 'MMMM_d_yyyy');
    const finalEndDateString = formattedEndDate.replace(/\s+/g, '_');
    return `${finalString}-${finalEndDateString}`;
  }
  if (timeframe === TimeFrameEnum.custom) {
    return customTimeframeParameters.split('&')[0];
  } else {
    return '';
  }
};
//*Validating possibility of  Download System Analysis Report
export const checkSystemAnalysisReportValidToDownloadAC = createAsyncAction(
  'IS_SYSTEM_ANALYSIS_REPORT_VALID_TO_DOWNLOAD_STARTED',
  'IS_SYSTEM_ANALYSIS_REPORT_VALID_TO_DOWNLOAD_SUCCESS',
  'IS_SYSTEM_ANALYSIS_REPORT_VALID_TO_DOWNLOAD_FAILED'
)<any, any, any>();

export const checkSystemAnalysisReportValidToDownload = ({
  week,
  day,
  custom,
  systems,
  SystemGroupType,
}: any) => {
  const url = makeApiUrl(`/api/job-analysis-report/check-${ERRORS_AND_MAINTENANCE_FILE_SEGMENT}`);
  const customTimeframeParameters = getCustomSearchParameters(custom);

  return apiAction({
    request: {
      url,
      method: 'POST',
      params: {
        week,
        day,
        custom: customTimeframeParameters,
        SystemGroupType,
      },
      data: {
        systems,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(
          checkSystemAnalysisReportValidToDownloadAC.failure({
            selectedSystemGroup: SystemGroupType,
            name: error.name,
            message: 'something went wrong, try again later',
          })
        );
      },
      onStarted: dispatch => {
        dispatch(
          checkSystemAnalysisReportValidToDownloadAC.request({
            selectedSystemGroup: SystemGroupType,
          })
        );
      },
      onSuccess: (data, dispatch, state, status) => {
        dispatch(
          checkSystemAnalysisReportValidToDownloadAC.success({
            selectedSystemGroup: SystemGroupType,
            status,
          })
        );
      },
    },
  });
};
